import MappingValueInput from "../MappingValueInput";
import { ClientMappingField, NOTION_PARENT_TYPE } from "@avenueops/shared-types";
import { SmartForm, DropdownMultiSelect } from "@avenueops/web-components";
import { ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import Tooltip from "components/generic-building-blocks/Tooltip";
import TemplateableAvenueDatePicker from "components/inputs/TemplateableAvenueDatePicker";
import UncontrolledInputSearchableSelect from "components/inputs/UncontrolledInputSearchableSelect";
import UncontrolledInputSelect from "components/inputs/UncontrolledInputSelect";
import Link from "next/link";
import { trpc2 } from "utils/trpc";

type NotionDatabasePropertyOptions = {
  id: string;
  name: string;
  color: string;
}[];

type NotionDatabaseProperties = {
  name: string;
  type: string;
  options?: NotionDatabasePropertyOptions | undefined;
};

interface Props {
  integrationId: string;
  recipientValue: string;
  fullFieldName: string;
  fieldValue: string;
  fieldError: any;
  onChange: any;
  isFirst: boolean;
  update: (_: ClientMappingField) => void;
  field: ClientMappingField;
  prefix: string;
}

export default function EnhancedNotionFieldValueMappingInput({
  integrationId,
  recipientValue,
  fullFieldName,
  fieldValue,
  fieldError,
  onChange,
  isFirst,
  update,
  field,
  prefix,
}: Props) {
  const { data } = trpc2.v2.notion.getNotionDatabase.useQuery({
    integrationId: integrationId,
    databaseId: recipientValue,
  });

  const augmentedFieldData = data?.find((props) => fullFieldName.includes(props.name));

  return fullFieldName === NOTION_PARENT_TYPE ? (
    <div>
      <UncontrolledInputSearchableSelect
        title={isFirst ? `Data in Avenue` : undefined}
        disabled={false}
        options={[
          { value: "database", label: "Database" },
          { value: "page", label: "Page" },
        ]}
        val={fieldValue}
        onChange={onChange}
        placeholder={"Parent Type"}
        error={fieldError}
        secondaryElement={<NotionInformationalTooltip fieldValue={fieldValue} />}
      />
    </div>
  ) : augmentedFieldData !== undefined ? (
    renderAugmentedFieldInput(augmentedFieldData, fieldValue, fieldError, onChange, update, field, isFirst, prefix)
  ) : (
    <MappingValueInput update={update} item={field} isFirst={isFirst} prefix={prefix} />
  );
}

function renderAugmentedFieldInput(
  data: NotionDatabaseProperties,
  fieldValue: string,
  fieldError: any,
  onChange: any,
  update: (_: ClientMappingField) => void,
  field: ClientMappingField,
  isFirst: boolean,
  prefix: string,
) {
  if (data.type === "select") {
    const filteredOptions = data.options ? data.options.map((option) => ({ value: option.name, label: option.name })) : [];
    return <SmartForm.DropdownSelect title="Value" name={`${prefix}.value`} options={filteredOptions} />;
  } else if (data.type === "multi_select") {
    const filteredOptions = data.options ? data.options.map((option) => ({ value: option.name, label: option.name })) : [];
    return (
      <UncontrolledInputSearchableSelect
        title="Values"
        options={filteredOptions}
        val={fieldValue}
        onChange={onChange}
        error={fieldError}
        placeholder={"Select options"}
        isMulti
        passOption
      />
    );
  } else if (data.type === "date") {
    return (
      <div className="mt-6">
        <TemplateableAvenueDatePicker onChange={onChange} value={fieldValue} rounded={false} includeHours={false} />
      </div>
    );
  } else if (data.type === "relation") {
    return <SmartForm.TextInput title="Values" placeholder="page_id_1,page_id" name={`${prefix}.value`} />;
  } else if (data.type === "people") {
    const filteredOptions = data.options ? data.options.map((user) => ({ value: user.id, label: user.name })) : [];
    return (
      <DropdownMultiSelect
        title="Values"
        placeholder="Enter people..."
        name={`${prefix}.value`}
        options={filteredOptions}
        selectedValues={[...fieldValue]}
        onChange={onChange}
        onCreate={(searchText: any) => {
          return { label: searchText, value: searchText };
        }}
      />
    );
  }
  return <MappingValueInput update={update} item={field} isFirst={isFirst} prefix={prefix} />;
}

function NotionInformationalTooltip({ fieldValue }: { fieldValue: string }) {
  return (
    <Tooltip text={<TooltipText fieldValue={fieldValue} />}>
      <InformationCircleIcon className="h-5 w-5" />
    </Tooltip>
  );
}

function TooltipText({ fieldValue }: { fieldValue: string }) {
  return (
    <div className="flex flex-row gap-x-1  text-sm text-white">
      <ExclamationTriangleIcon className="h-5 w-5 cursor-pointer" />
      {fieldValue === "database" ? (
        <span>
          Database objects describe the property schema of a database in Notion.{" "}
          <strong>
            <Link href="https://developers.notion.com/reference/page">Pages</Link>
          </strong>{" "}
          are the items (or children) in a database.{" "}
          <strong>
            <Link href="https://developers.notion.com/reference/page#property-value-object">Page property values</Link>
          </strong>{" "}
          must conform to the
          <strong>
            {" "}
            <Link href="https://developers.notion.com/reference/property-object">property objects</Link>
          </strong>{" "}
          laid out in the parent database object.
        </span>
      ) : (
        <span>
          The Page object contains the{" "}
          <strong>
            <Link href="https://developers.notion.com/reference/page#property-value-object">page property values</Link>
          </strong>{" "}
          of a single Notion page.
        </span>
      )}
    </div>
  );
}
